import { CaretDown, Check, Function, Table } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import MarketingHeading from "../../common/MarketingHeading"
import NicerBlockquote from "../../common/NicerBlockquote"
import ProductPageHeader from "../ProductPageHeader"
import ProductPageTemplate from "../ProductPageTemplate"
import { CustomerLogoStrip, FeatureCard, StatsGrid, ThreeFeaturesSection } from "../shared"
import InlineCode from "./InlineCode"
import GoogleSheetsLogo from "./logos/GoogleSheetsLogo"
import MicrosoftExcelLogo from "./logos/MicrosoftExcelLogo"

// Simple TT Function Demo - Shows basic =TT function for single metrics
function SimpleTTDemo() {
  const columns = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"]
  const rows = Array.from({ length: 15 }, (_, i) => i + 1)

  const spreadsheetData: Record<string, Record<number, string>> = {
    A: {
      1: "Project",
      2: "Ethereum",
      3: "Solana",
      4: "Arbitrum",
      5: "Base",
      6: "Optimism",
      7: "Polygon",
      8: "Avalanche",
      9: "BNB Chain",
      10: "Fantom",
      11: "Sui",
      12: "Aptos",
      13: "Near",
      14: "Cosmos",
      15: "Polkadot",
    },
    B: {
      1: "Revenue",
      2: "$2.4B",
      3: "$892M",
      4: "$156M",
      5: "$89M",
      6: "$67M",
      7: "$45M",
      8: "$34M",
      9: "$28M",
      10: "$12M",
      11: "$8.5M",
      12: "$6.2M",
      13: "$4.8M",
      14: "$3.2M",
      15: "$2.1M",
    },
    C: {
      1: "Fees",
      2: "$8.1B",
      3: "$1.2B",
      4: "$312M",
      5: "$178M",
      6: "$134M",
      7: "$89M",
      8: "$67M",
      9: "$56M",
      10: "$23M",
      11: "$17M",
      12: "$12M",
      13: "$9.6M",
      14: "$6.4M",
      15: "$4.2M",
    },
    D: {
      1: "TVL",
      2: "$48.2B",
      3: "$4.8B",
      4: "$2.9B",
      5: "$1.8B",
      6: "$890M",
      7: "$780M",
      8: "$520M",
      9: "$3.2B",
      10: "$89M",
      11: "$1.2B",
      12: "$890M",
      13: "$340M",
      14: "$890M",
      15: "$120M",
    },
    E: {
      1: "Market Cap",
      2: "$380B",
      3: "$89B",
      4: "$3.2B",
      5: "-",
      6: "$2.1B",
      7: "$4.8B",
      8: "$12B",
      9: "$89B",
      10: "$1.2B",
      11: "$3.4B",
      12: "$4.1B",
      13: "$5.6B",
      14: "$8.9B",
      15: "$6.7B",
    },
  }

  function getCellContent(col: string, row: number): string {
    return spreadsheetData[col]?.[row] || ""
  }

  function isNumericCell(col: string, row: number): boolean {
    if (col === "A") return false
    if (row === 1) return false
    return !!getCellContent(col, row)
  }

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl"
      style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
    >
      <div className="bg-bg-default" style={{ width: "calc(100% + 200px)" }}>
        {/* Top Header Bar */}
        <div className="flex items-center gap-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <div className="flex items-center gap-1 px-2 py-1.5 bg-white dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800">
            <input
              type="text"
              value="B2"
              readOnly
              className="w-12 text-xs font-medium text-fg-default bg-transparent border-none outline-none cursor-pointer"
            />
            <button className="text-fg-secondary hover:text-fg-default">
              <CaretDown className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-neutral-950">
            <Function className="w-4 h-4 text-fg-secondary" />
            <div className="flex-1 font-mono text-sm">
              <span className="text-fg-default">=TT(</span>
              <span className="text-green-600 dark:text-green-400">&quot;ETH&quot;</span>
              <span className="text-fg-default">, </span>
              <span className="text-green-600 dark:text-green-400">&quot;revenue&quot;</span>
              <span className="text-fg-default">)</span>
            </div>
          </div>
        </div>

        {/* Spreadsheet Grid */}
        <div className="flex">
          <div className="w-12 shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
            <div className="border-b border-neutral-200 dark:border-neutral-800">
              <div style={{ height: "32px" }} />
            </div>
            {rows.map((rowNum) => (
              <div key={rowNum} className="border-b border-neutral-200 dark:border-neutral-800">
                <div
                  className={`flex items-center justify-center text-xs font-medium ${
                    rowNum === 1
                      ? "bg-neutral-200 dark:bg-neutral-800"
                      : "bg-neutral-50 dark:bg-neutral-900"
                  }`}
                  style={{ height: "32px" }}
                >
                  {rowNum}
                </div>
              </div>
            ))}
          </div>

          <div className="flex-1">
            <div className="flex border-b border-neutral-200 dark:border-neutral-800">
              {columns.map((col) => (
                <div
                  key={col}
                  className={`w-32 shrink-0 flex items-center justify-center text-xs border-r border-neutral-200 dark:border-neutral-800 ${
                    col === "B" ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-neutral-950"
                  }`}
                  style={{ height: "32px" }}
                >
                  {col}
                </div>
              ))}
            </div>

            <div className="flex flex-col">
              {rows.map((rowNum) => (
                <div
                  key={rowNum}
                  className="flex border-b border-neutral-200 dark:border-neutral-800"
                >
                  {columns.map((col) => {
                    const isB2 = col === "B" && rowNum === 2
                    const cellContent = getCellContent(col, rowNum)
                    const isNumeric = isNumericCell(col, rowNum)

                    return (
                      <div
                        key={`${col}${rowNum}`}
                        className={`w-32 shrink-0 flex items-center px-2 text-xs border-r border-neutral-200 dark:border-neutral-800 relative ${
                          isB2 ? "bg-blue-50 dark:bg-blue-950/20" : "bg-white dark:bg-neutral-950"
                        } ${isNumeric ? "justify-end" : "justify-start"}`}
                        style={{ height: "32px" }}
                      >
                        {isB2 && (
                          <>
                            <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none z-10" />
                            <div className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 cursor-nwse-resize z-20" />
                          </>
                        )}
                        {cellContent && (
                          <span className="text-xs relative z-0 text-fg-default">
                            {cellContent}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Financial Statement Demo - Shows =TT_FINANCIAL_STATEMENT function
function FinancialStatementDemo() {
  const columns = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"]
  const rows = Array.from({ length: 15 }, (_, i) => i + 1)

  const spreadsheetData: Record<string, Record<number, string>> = {
    A: {
      1: "Quarters",
      2: "Income Statement",
      3: "Fees",
      4: "Fees_Supply_Side",
      5: "Revenue",
      6: "Expenses",
      7: "Token_Incentives",
      8: "Earnings",
      9: "Market Data",
      10: "Price",
      11: "Token_Supply_Circ",
      12: "Market_Cap_Fully",
      13: "Market_Cap_Circ",
      14: "Token_Trading_Vol",
      15: "Token_Turnover",
    },
    B: {
      1: "Q1 2026",
      3: "$4,716,702.18",
      4: "$4,010,677.26",
      5: "$706,024.92",
      6: "$97,171,161.57",
      7: "$97,171,161.57",
      8: "($96,465,136.64)",
      10: "$3,144.27",
      11: "120,694,799.00",
      12: "$379,497,059,41",
      13: "$379,514,107,04",
      14: "$258,735,034,58",
      15: "5.2%",
    },
    C: {
      1: "Q4 2025",
      3: "$76,566,498.54",
      4: "$48,093,226.99",
      5: "$28,473,271.56",
      6: "$765,529,137.32",
      7: "$765,529,137.32",
      8: "($737,055,865.7)",
      10: "$3,445.56",
      11: "120,697,094.43",
      12: "$415,870,738,66",
      13: "$415,923,051,79",
      14: "$2,887,365,690,",
      15: "7.49%",
    },
    D: {
      1: "Q3 2025",
      3: "$125,341,114.86",
      4: "$69,046,943.74",
      5: "$56,294,171.12",
      6: "$933,698,411.71",
      7: "$933,698,411.71",
      8: "($877,404,240.5)",
      10: "$3,935.66",
      11: "120,708,873.17",
      12: "$475,066,746,72",
      13: "$475,072,761,90",
      14: "$3,089,332,461,",
      15: "7.11%",
    },
    E: {
      1: "Q2 2025",
      3: "$103,303,663.73",
      4: "$52,989,953.17",
      5: "$50,313,710.56",
      6: "$528,485,214.55",
      7: "$528,485,214.55",
      8: "($478,171,503.9)",
      10: "$2,194.53",
      11: "120,714,917.74",
      12: "$264,917,797,95",
      13: "$264,920,345,61",
      14: "$1,777,558,820,",
      15: "7.49%",
    },
    F: {
      1: "Q1 2025",
      3: "$220,802,688.72",
      4: "$69,156,086.76",
      5: "$151,646,601.96",
      6: "$635,839,926.31",
      7: "$635,839,926.31",
      8: "($484,193,324.3)",
      10: "$2,671.18",
      11: "120,554,621.84",
      12: "$321,992,815,91",
      13: "$321,952,250,43",
      14: "$2,188,243,821,",
      15: "7.65%",
    },
    G: {
      1: "Q4 2024",
      3: "$551,843,538.53",
      4: "$119,717,350.17",
      5: "$432,126,188.36",
      6: "$758,291,013.52",
      7: "$758,291,013.52",
      8: "($326,164,825.1)",
      10: "$3,092.57",
      11: "120,421,183.37",
      12: "$372,423,952,12",
      13: "$372,363,206,49",
      14: "$2,742,981,798,",
      15: "7.73%",
    },
    H: {
      1: "Q3 2024",
      3: "$261,174,551.17",
      4: "$101,770,867.32",
      5: "$159,403,683.85",
      6: "$680,498,577.13",
      7: "$680,498,577.13",
      8: "($521,094,893.2)",
      10: "$2,797.27",
      11: "120,277,872.73",
      12: "$336,433,963,40",
      13: "$336,460,049,94",
      14: "$1,435,910,894,",
      15: "4.73%",
    },
    I: {
      1: "Q2 2024",
      3: "$492,813,548.78",
      4: "$124,212,725.29",
      5: "$368,600,823.49",
      6: "$792,965,097.90",
      7: "$792,965,097.90",
      8: "($424,364,274.41)",
      10: "$3,373.36",
      11: "120,451,228.38",
      12: "$406,304,823,873.42",
      13: "$406,356,281,077.86",
      14: "$1,332,183,649,932.74",
      15: "3.61%",
    },
  }

  function getCellContent(col: string, row: number): string {
    return spreadsheetData[col]?.[row] || ""
  }

  function isNumericCell(col: string, row: number): boolean {
    if (col === "A") return false
    if (row === 1) return false
    return !!getCellContent(col, row)
  }

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl"
      style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
    >
      <div className="bg-bg-default" style={{ width: "calc(100% + 200px)" }}>
        {/* Top Header Bar */}
        <div className="flex items-center gap-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <div className="flex items-center gap-1 px-2 py-1.5 bg-white dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800">
            <input
              type="text"
              value="A1"
              readOnly
              className="w-12 text-xs font-medium text-fg-default bg-transparent border-none outline-none cursor-pointer"
            />
            <button className="text-fg-secondary hover:text-fg-default">
              <CaretDown className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-neutral-950">
            <Function className="w-4 h-4 text-fg-secondary" />
            <div className="flex-1 font-mono text-sm">
              <span className="text-fg-default">=TT_FINANCIAL_STATEMENT(</span>
              <span className="text-green-600 dark:text-green-400">&quot;ETH&quot;</span>
              <span className="text-fg-default">, </span>
              <span className="text-green-600 dark:text-green-400">&quot;quarter&quot;</span>
              <span className="text-fg-default">, </span>
              <span className="text-green-600 dark:text-green-400">&quot;2y&quot;</span>
              <span className="text-fg-default">)</span>
            </div>
          </div>
        </div>

        {/* Spreadsheet Grid */}
        <div className="flex">
          <div className="w-12 shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
            <div className="border-b border-neutral-200 dark:border-neutral-800">
              <div style={{ height: "32px" }} />
            </div>
            {rows.map((rowNum) => (
              <div key={rowNum} className="border-b border-neutral-200 dark:border-neutral-800">
                <div
                  className={`flex items-center justify-center text-xs font-medium ${
                    rowNum === 1
                      ? "bg-neutral-200 dark:bg-neutral-800"
                      : "bg-neutral-50 dark:bg-neutral-900"
                  }`}
                  style={{ height: "32px" }}
                >
                  {rowNum}
                </div>
              </div>
            ))}
          </div>

          <div className="flex-1">
            <div className="flex border-b border-neutral-200 dark:border-neutral-800">
              {columns.map((col) => (
                <div
                  key={col}
                  className={`w-32 shrink-0 flex items-center justify-center text-xs border-r border-neutral-200 dark:border-neutral-800 ${
                    col === "A" ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-neutral-950"
                  }`}
                  style={{ height: "32px" }}
                >
                  {col}
                </div>
              ))}
            </div>

            <div className="flex flex-col">
              {rows.map((rowNum) => (
                <div
                  key={rowNum}
                  className="flex border-b border-neutral-200 dark:border-neutral-800"
                >
                  {columns.map((col) => {
                    const isA1 = col === "A" && rowNum === 1
                    const cellContent = getCellContent(col, rowNum)
                    const isNumeric = isNumericCell(col, rowNum)

                    return (
                      <div
                        key={`${col}${rowNum}`}
                        className={`w-32 shrink-0 flex items-center px-2 text-xs border-r border-neutral-200 dark:border-neutral-800 relative ${
                          isA1 ? "bg-blue-50 dark:bg-blue-950/20" : "bg-white dark:bg-neutral-950"
                        } ${isNumeric ? "justify-end" : "justify-start"}`}
                        style={{ height: "32px" }}
                      >
                        {isA1 && (
                          <>
                            <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none z-10" />
                            <div className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 cursor-nwse-resize z-20" />
                          </>
                        )}
                        {cellContent && (
                          <span className="text-xs relative z-0 text-fg-default">
                            {cellContent}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Time Series Demo - Shows =TT_TIMESERIES function
function TimeSeriesDemo() {
  const columns = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"]
  const rows = Array.from({ length: 15 }, (_, i) => i + 1)

  const spreadsheetData: Record<string, Record<number, string>> = {
    A: {
      1: "Date",
      2: "2022-01-01",
      3: "2022-01-02",
      4: "2022-01-03",
      5: "2022-01-04",
      6: "2022-01-05",
      7: "2022-01-06",
      8: "2022-01-07",
      9: "2022-01-08",
      10: "2022-01-09",
      11: "2022-01-10",
      12: "2022-01-11",
      13: "2022-01-12",
      14: "2022-01-13",
      15: "2022-01-14",
    },
    B: {
      1: "revenue",
      2: "$42,156,789",
      3: "$38,234,521",
      4: "$45,891,234",
      5: "$39,123,456",
      6: "$51,789,012",
      7: "$47,456,789",
      8: "$43,234,567",
      9: "$48,901,234",
      10: "$44,543,210",
      11: "$52,654,321",
      12: "$49,765,432",
      13: "$46,876,543",
      14: "$41,123,456",
      15: "$53,234,567",
    },
    C: {
      1: "fees",
      2: "$89,456,123",
      3: "$82,123,456",
      4: "$95,789,012",
      5: "$78,890,123",
      6: "$102,345,678",
      7: "$91,012,345",
      8: "$86,567,890",
      9: "$97,789,012",
      10: "$88,234,567",
      11: "$105,678,901",
      12: "$99,890,123",
      13: "$92,987,654",
      14: "$84,345,678",
      15: "$108,876,543",
    },
    D: {
      1: "users_dau",
      2: "412,567",
      3: "398,234",
      4: "425,891",
      5: "387,123",
      6: "451,789",
      7: "434,456",
      8: "408,234",
      9: "445,901",
      10: "421,543",
      11: "468,654",
      12: "452,765",
      13: "439,876",
      14: "395,123",
      15: "475,234",
    },
  }

  function getCellContent(col: string, row: number): string {
    return spreadsheetData[col]?.[row] || ""
  }

  function isNumericCell(col: string, row: number): boolean {
    if (col === "A") return false
    if (row === 1) return false
    return !!getCellContent(col, row)
  }

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl"
      style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
    >
      <div className="bg-bg-default" style={{ width: "calc(100% + 200px)" }}>
        {/* Top Header Bar */}
        <div className="flex items-center gap-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
          <div className="flex items-center gap-1 px-2 py-1.5 bg-white dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800">
            <input
              type="text"
              value="A1"
              readOnly
              className="w-12 text-xs font-medium text-fg-default bg-transparent border-none outline-none cursor-pointer"
            />
            <button className="text-fg-secondary hover:text-fg-default">
              <CaretDown className="w-3 h-3" />
            </button>
          </div>
          <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-neutral-950">
            <Function className="w-4 h-4 text-fg-secondary" />
            <div className="flex-1 font-mono text-sm">
              <span className="text-fg-default">=TT_TIMESERIES(</span>
              <span className="text-green-600 dark:text-green-400">&quot;eth&quot;</span>
              <span className="text-fg-default">, {"{"}</span>
              <span className="text-green-600 dark:text-green-400">&quot;revenue&quot;</span>
              <span className="text-fg-default">, </span>
              <span className="text-green-600 dark:text-green-400">&quot;fees&quot;</span>
              <span className="text-fg-default">, </span>
              <span className="text-green-600 dark:text-green-400">&quot;users_dau&quot;</span>
              <span className="text-fg-default">{"}"}, </span>
              <span className="text-green-600 dark:text-green-400">&quot;2022-01-01&quot;</span>
              <span className="text-fg-default">, </span>
              <span className="text-green-600 dark:text-green-400">&quot;2022-10-01&quot;</span>
              <span className="text-fg-default">)</span>
            </div>
          </div>
        </div>

        {/* Spreadsheet Grid */}
        <div className="flex">
          <div className="w-12 shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
            <div className="border-b border-neutral-200 dark:border-neutral-800">
              <div style={{ height: "32px" }} />
            </div>
            {rows.map((rowNum) => (
              <div key={rowNum} className="border-b border-neutral-200 dark:border-neutral-800">
                <div
                  className={`flex items-center justify-center text-xs font-medium ${
                    rowNum === 1
                      ? "bg-neutral-200 dark:bg-neutral-800"
                      : "bg-neutral-50 dark:bg-neutral-900"
                  }`}
                  style={{ height: "32px" }}
                >
                  {rowNum}
                </div>
              </div>
            ))}
          </div>

          <div className="flex-1">
            <div className="flex border-b border-neutral-200 dark:border-neutral-800">
              {columns.map((col) => (
                <div
                  key={col}
                  className={`w-32 shrink-0 flex items-center justify-center text-xs border-r border-neutral-200 dark:border-neutral-800 ${
                    col === "A" ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-neutral-950"
                  }`}
                  style={{ height: "32px" }}
                >
                  {col}
                </div>
              ))}
            </div>

            <div className="flex flex-col">
              {rows.map((rowNum) => (
                <div
                  key={rowNum}
                  className="flex border-b border-neutral-200 dark:border-neutral-800"
                >
                  {columns.map((col) => {
                    const isA1 = col === "A" && rowNum === 1
                    const cellContent = getCellContent(col, rowNum)
                    const isNumeric = isNumericCell(col, rowNum)

                    return (
                      <div
                        key={`${col}${rowNum}`}
                        className={`w-32 shrink-0 flex items-center px-2 text-xs border-r border-neutral-200 dark:border-neutral-800 relative ${
                          isA1 ? "bg-blue-50 dark:bg-blue-950/20" : "bg-white dark:bg-neutral-950"
                        } ${isNumeric ? "justify-end" : "justify-start"}`}
                        style={{ height: "32px" }}
                      >
                        {isA1 && (
                          <>
                            <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none z-10" />
                            <div className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 cursor-nwse-resize z-20" />
                          </>
                        )}
                        {cellContent && (
                          <span className="text-xs relative z-0 text-fg-default">
                            {cellContent}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SheetsPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Table}
          title="Sheets"
          subtitle="Sheets brings Token Terminal's metrics directly into Google Sheets and Excel with simple, powerful functions."
          ctaButtons={
            <div className="flex flex-col gap-3">
              <p className="text-sm text-fg-secondary">Install free for Google Sheets or Excel</p>
              <div className="flex flex-row items-center gap-3">
                <LinkButton
                  key="google-sheets-plugin"
                  size="large"
                  href="https://workspace.google.com/marketplace/app/token_terminal_for_sheets/702043887467"
                  target="_blank"
                  style={{ zIndex: 4 }}
                  className="flex items-center gap-2 px-4"
                >
                  <GoogleSheetsLogo className="h-[26px]" />
                </LinkButton>
                <LinkButton
                  key="excel-plugin"
                  size="large"
                  href="https://appsource.microsoft.com/en-us/product/office/WA200007719"
                  target="_blank"
                  style={{ zIndex: 4 }}
                  className="flex items-center gap-2 px-4"
                >
                  <MicrosoftExcelLogo className="h-[24px]" />
                </LinkButton>
                <LinkButton key="view-pricing" size="large" href="/pricing" style={{ zIndex: 4 }}>
                  View pricing
                </LinkButton>
              </div>
            </div>
          }
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/products/sheets/product-hero-sheets2.webp`}
          whoWeServeText="Analysts and fund managers who build financial models and reports in a spreadsheet environment."
          simpleWhoWeServeText="analysts and fund managers"
        />
      }
      productId="sheets"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip logos={productPageLogos.sheets} />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <SimpleTTDemo />
              </FeatureCard>
            ),
            title: (
              <>
                Simple functions,
                <br />
                powerful data.
              </>
            ),
            description: (
              <>
                Pull any metric with <InlineCode>=TT</InlineCode>. No API keys, no complex setup.
                Just type the metric name and get the correct data.
              </>
            ),
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <FinancialStatementDemo />
              </FeatureCard>
            ),
            title: "Financial statements in one formula.",
            description: (
              <>
                Generate complete financial statements with{" "}
                <InlineCode>=TT_FINANCIAL_STATEMENT</InlineCode>. Ready for rich data financial
                models.
              </>
            ),
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <TimeSeriesDemo />
              </FeatureCard>
            ),
            title: "Historical data for any time range.",
            description: (
              <>
                Pull any time series with <InlineCode>=TT_TIME_SERIES</InlineCode>. Build backtests,
                analyze trends, and compare performance.
              </>
            ),
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="Token Terminal has been an invaluable tool in my crypto research, offering deep insights into on-chain metrics and token economics. Its user-friendly interface and robust data sets streamline my analysis, ensuring I stay ahead in the rapidly evolving crypto landscape."
          author="Ryan Rasmussen"
          authorTitle="Bitwise"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-RasterlyRock.jpg`}
        />
      </div>

      {/* CTA Section */}
      <div className="max-w-[900px] mx-auto mt-72 mb-24">
        <div className="flex items-stretch gap-12 flex-col md:flex-row">
          <div>
            <div className="flex flex-col gap-5">
              <MarketingHeading level={2} className="text-2xl">
                Get started for free
              </MarketingHeading>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Access dashboards for 1000s of projects
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Derive insights from prebuilt financial statements
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Try out Sheets with 5,000 requests per month
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-start gap-2">
                <LinkButton className="w-full mt-4" size="large" href="/explorer">
                  Get started for free
                </LinkButton>
              </div>
            </div>
          </div>
          <div className="border-l border-border-default" style={{ width: "1px" }} />
          <div>
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2">
                <Badge color="green" variant="solid" size="small" className="shrink-0">
                  PRO
                </Badge>
                <MarketingHeading level={2} className="text-2xl">
                  Use Sheets at scale
                </MarketingHeading>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">500,000 Sheets requests per month</div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Access to Google Sheets and Excel plugins
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Pull metrics and historical data directly
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-start gap-2">
                <LinkButton className="w-full mt-4" size="large" href="/pricing" color="success">
                  Subscribe to Pro
                </LinkButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductPageTemplate>
  )
}

export default SheetsPage
